import { Can } from '@/components/can/Can';
import { Form, useForm } from '@/components/forms';
import DatePicker from '@/components/forms/DatePicker';
import ImagePreview from '@/components/forms/imagePreview';
import Input from '@/components/forms/Input';
import RadioGroup from '@/components/forms/RadioGroup';
import Select from '@/components/forms/Select';
import { Upload } from '@/components/upload';
import { nameValidationRegex } from '@/constants/regex';
import { Separator } from '@/design-system/separator';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { usePostalCodeQuery } from '@/hooks/use-postal-code';
import { useDropdown } from '@/hooks/useDropdown';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { commaSeparator } from '@/lib/commaSeparator';
import { requiredImageValueSchema } from '@/lib/uploadSchema';
import { FileObject, uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { useMutation } from '@/react-query/hooks/useMutation/useMutation';
import { AddressData } from '@/services/customers/customers.types';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from 'ui/button';
import { z } from 'zod';
import { useCustomerDetail } from './hooks/useCustomerDetail';

declare global {
  interface Window {
    yubin: { [key: string]: AddressData };
  }
}

type ProfileImageType = {
  alt: string;
  description: string;
  path: string;
};

const CustomerFormSchema = z.object({
  username: z.string().min(1, 'common.form.validation.required'),
  fullName: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .refine((val) => val === '' || nameValidationRegex.test(val), { message: 'common.form.validation.nameOnly' }),

  customerNameFurigana: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .refine((val) => val === '' || nameValidationRegex.test(val), { message: 'common.form.validation.nameOnly' }),
  mobileNumber: z.string(),

  emailAddress: z.string().min(1, 'common.form.validation.required'),
  dob: z.string().trim().min(1, 'common.form.validation.required'),
  gender: z.enum(['M', 'F', 'O']),
  profileImage: requiredImageValueSchema,
  postalCode: z
    .string()
    .min(1, 'common.form.validation.required')
    .min(8, 'common.form.validation.invalidPostalCode')
    .max(8, 'common.form.validation.invalidPostalCode')
    .transform((val) => val.replace(/\D/g, ''))
    .transform((val) => `${val.slice(0, 3)}-${val.slice(3)}`),
  prefecture: z.string().refine((value) => value !== '' && value !== 'default', {
    message: 'common.form.validation.required',
  }),
  city: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
  street: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
  buildingNo: z
    .string()
    .trim()
    .refine((val) => val === '' || nameValidationRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    })
    .optional(),
});

export const CustomerForm = ({ customerId }: { customerId: string }) => {
  const { customersDetail } = useCustomerDetail(customerId);

  const form = useForm({
    schema: CustomerFormSchema,
    mode: 'onBlur',

    defaultValues: {
      username: '',
      gender: undefined,
      profileImage: undefined,
      fullName: '',
      customerNameFurigana: '',
      mobileNumber: '',
      emailAddress: '',
      dob: '',
      postalCode: '',
      prefecture: '',
      city: '',
      street: '',
      buildingNo: '',
    },
  });
  const postalCodeValue = form.watch('postalCode') || customersDetail?.postalCode;
  const { refetch } = usePostalCodeQuery(postalCodeValue || '');

  const navigate = useNavigate();
  const onCancel = () => {
    void navigate({ to: '/users/customers' });
  };
  const { t } = useDynamicLocale();

  const Gender: Record<NonNullable<z.infer<typeof CustomerFormSchema>['gender']>, string> = {
    M: t('affiliate.page.affiliateInfoForm.male'),
    F: t('affiliate.page.affiliateInfoForm.female'),
    O: t('affiliate.page.affiliateInfoForm.others'),
  };

  const genderOptions = Object.entries(Gender).map(([value, label]) => ({
    value,
    label,
  }));

  const fetchAddressByPostalCode = () => {
    void refetch().then((res) => {
      if (res.data) {
        form.setValue('city', res.data.address2, {
          shouldValidate: true,
        });
        form.setValue('street', res.data.address3, {
          shouldValidate: true,
        });
      } else {
        form.setError('postalCode', {
          type: 'manual',
          message: 'Postal code is invalid',
        });
      }
    });
  };

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation('manageCustomer', {
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success(response.message);
      void navigate({
        to: `/users/customers`,
      });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  useEffect(() => {
    if (customersDetail && Object.keys(customersDetail).length > 0) {
      form.reset({
        username: customersDetail.username || '',
        customerNameFurigana: customersDetail.customerNameFurigana || '',
        mobileNumber: customersDetail.mobileNumber || '',
        emailAddress: customersDetail.emailAddress || '',
        gender: customersDetail.gender || 'M',
        city: customersDetail.city || '',
        street: customersDetail.street || '',
        buildingNo: customersDetail.buildingNo || '',
        postalCode: customersDetail.postalCode || '',
        prefecture: customersDetail.prefecture || '',
        fullName: `${customersDetail.firstName || ''} ${customersDetail.lastName || ''}`,
        dob:
          typeof customersDetail.dob === 'number' && customersDetail.dob > 0
            ? new Date(customersDetail.dob).toISOString()
            : '',
        profileImage: customersDetail.image,
      });
    }
  }, [customersDetail]);

  const [profileImage, setProfileImage] = useState<ProfileImageType>({
    alt: '',
    description: '',
    path: '',
  });

  useEffect(() => {
    if (customersDetail.image?.path) {
      setProfileImage((prev) => ({ ...prev, path: customersDetail.image?.path || '' }));
    }
  }, [customersDetail.image]);
  const { data: prefectureOption = [] } = useDropdown('Location');

  return (
    <Form
      form={form}
      onSubmit={async () => {
        const formData = form.getValues();
        const fullName = (formData.fullName || '').trim();
        const [firstName = '', ...rest] = fullName.split(' ');
        const lastName = rest.join(' ');
        let imageUrl = '';

        if ('preview' in formData.profileImage && formData.profileImage.preview) {
          const imageFile = formData.profileImage?.valueOf();
          imageUrl = await uploadFileToS3WithPresignedUrl((imageFile as FileObject) || undefined);
        }

        mutate(
          {
            customerId: customerId || '',
            username: formData.username || '',
            firstName: firstName ?? customersDetail?.firstName ?? '',
            lastName: lastName ?? customersDetail?.lastName ?? '',
            mobileNumber: formData.mobileNumber,
            emailAddress: formData.emailAddress,
            gender: formData.gender,
            dob: formData.dob ? Number(new Date(formData.dob).getTime()) : 0,
            postalCode: formData.postalCode,
            prefecture: formData.prefecture,
            customerNameFurigana: formData.customerNameFurigana,
            city: formData.city,
            street: formData.street,
            buildingNo: formData.buildingNo,
            profileImage: profileImage.path || imageUrl || '',
          },
          {},
        );
      }}
    >
      <fieldset disabled={Object.keys(customersDetail).length < 8}>
        <div className="flex flex-col gap-6">
          <div className="text-sm bg-info inline-flex p-3 text-on-color rounded w-fit">
            <Translation id="affiliate.page.affiliateInfoForm.reward" />:{' '}
            {customersDetail.rewardPoints ? commaSeparator(customersDetail.rewardPoints) : '0'} 円
          </div>
          <div className="grid w-full gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ">
            <div className="h-[200px] w-full md:col-span-2 md:max-w-[308px] pl-12.5 md:mx-auto  lg:col-span-1 lg:max-w-none lg:mx-0">
              {profileImage.path ? (
                <ImagePreview
                  imageSrc={`${profileImage.path}?v=${Date.now()}`}
                  alt={profileImage.alt}
                  variant="circle"
                  cancelClickHandler={() => {
                    setProfileImage({
                      alt: '',
                      description: '',
                      path: '',
                    });
                    form.setValue('profileImage', {
                      alt: '',
                      description: '',
                      path: '',
                    });
                  }}
                />
              ) : (
                <Controller
                  name="profileImage"
                  control={form.control}
                  rules={{ required: 'Please upload a profile picture' }}
                  render={({ field, fieldState }) => (
                    <Upload
                      variant="circle"
                      accept=".jpg,.jpeg,.png,.svg,.heic,.heif"
                      multiple={false}
                      maxSizeMB={5}
                      error={fieldState.error?.message}
                      onFilesChange={(files) => {
                        return field.onChange(
                          files?.[0] ?? {
                            alt: '',
                            description: '',
                            path: '',
                          },
                        );
                      }}
                    />
                  )}
                />
              )}
            </div>
            <div className="grid grid-cols-2  col-span-2 gap-4">
              <Input
                type="text"
                disabled
                label={'affiliate.page.affiliateInfoForm.userName.label'}
                {...form.register('username')}
              />
              <div></div>
              <Input
                type="text"
                label={'affiliate.page.affiliateInfoForm.fullName.label'}
                {...form.register('fullName')}
                maxLength={50}
                placeholder={t('affiliate.page.affiliateInfoForm.fullName.placeholder')}
              />
              <Input
                type="text"
                label={'affiliate.page.affiliateInfoForm.furigana.label'}
                {...form.register('customerNameFurigana')}
                maxLength={50}
                placeholder={t('common.form.placeholder.furiganaPlaceholder')}
              />
              <Input
                type="text"
                label={'affiliate.page.affiliateInfoForm.mobileNumber.label'}
                disabled
                {...form.register('mobileNumber')}
              />
              <Input
                type="text"
                label={'affiliate.page.affiliateInfoForm.email.label'}
                disabled
                {...form.register('emailAddress')}
              />

              <DatePicker
                label={'affiliate.page.affiliateInfoForm.dob.label'}
                {...form.register('dob')}
                require18Years={true}
              />
              <div>
                <div className="xl:col-span-1 flex flex-wrap">
                  <RadioGroup
                    className="text-sm font-medium"
                    labelClassName="font-bold"
                    groupLabel={t('affiliate.page.affiliateInfoForm.gender.label')}
                    name="gender"
                    label={'Gender'}
                    options={genderOptions}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="grid w-full gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ">
            <div className="lg:pl-12.5 text-primary font-bold">
              <Translation id="affiliate.page.affiliateInfoForm.address" />
            </div>
            <div className="grid grid-cols-2 col-span-2 gap-4">
              <div className="flex flex-col">
                <Input
                  type="text"
                  placeholder="000-0000"
                  label={'affiliate.page.affiliateInfoForm.postalCode.label'}
                  maxLength={8}
                  {...form.register('postalCode', {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      let value = e.target.value;
                      value = value.replace(/\D/g, '');
                      if (value.length > 3) {
                        value = value.slice(0, 3) + '-' + value.slice(3, 7);
                      }
                      form.setValue('postalCode', value);
                    },
                  })}
                />
              </div>
              <div className="flex items-start pt-6">
                <Button
                  className="w-fit text-copy rounded-lg"
                  type="button"
                  onClick={() => void fetchAddressByPostalCode()}
                  disabled={(postalCodeValue?.length ?? 0) < 8}
                >
                  <Translation id="affiliate.page.affiliateInfoForm.autoFill" />
                </Button>
              </div>
              <Select
                options={prefectureOption}
                {...form.register('prefecture')}
                forceRequired
                label="affiliate.page.affiliateInfoForm.prefecture.label"
                selected={customersDetail?.prefecture}
              />
              <Input
                className="row-span-4"
                type="text"
                label={'affiliate.page.affiliateInfoForm.city.label'}
                {...form.register('city')}
                maxLength={50}
                placeholder={t('common.form.placeholder.enterCity')}
              />
              <Input
                className="row-span-4"
                type="text"
                label={'affiliate.page.affiliateInfoForm.street.label'}
                {...form.register('street')}
                maxLength={50}
                placeholder={t('common.form.placeholder.enterStreet')}
              />
              <Input
                className="row-span-4"
                type="text"
                label={'affiliate.page.affiliateInfoForm.buildingAndRoom.label'}
                {...form.register('buildingNo')}
                maxLength={50}
                placeholder={t('common.form.placeholder.enterBuilding')}
              />
            </div>
          </div>
        </div>
        <Separator className="mt-8 mb-6" variant="muted" thickness="lg" />

        <div className="flex w-full justify-between ">
          <Button type="button" variant="outline" onClick={onCancel} className="w-[140px] h-10 bg-card border-input">
            <Translation id="affiliate.page.affiliateInfoForm.cancel" />
          </Button>
          <Can rule={{ anyOf: [PERMS.customer.manage] }}>
            <Button type="submit" className="w-[110px] h-10" isLoading={isPending}>
              <Translation id="affiliate.page.affiliateInfoForm.save" />
            </Button>
          </Can>
        </div>
      </fieldset>
    </Form>
  );
};
